"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { Button, buttonVariants } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/components/ui/collapsible";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { toast } from "@shared/components/ui/snackbar";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAdminForceDeleteUser } from "@/lib/admin/users.hooks";
import { cn } from "@/lib/utils";

interface AdminUsersDangerZoneProps {
  userId: string;
  email: string;
  onDeleted: () => void;
}

export const AdminUsersDangerZone = ({ userId, email, onDeleted }: AdminUsersDangerZoneProps) => {
  const queryClient = useQueryClient();
  const forceDelete = useAdminForceDeleteUser();

  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const canDelete = confirmed && emailInput.trim().toLowerCase() === email.trim().toLowerCase();

  const handleForceDelete = () => {
    forceDelete.mutate(
      { id: userId, email_confirmation: emailInput.trim() },
      {
        onSuccess: () => {
          toast.success("User permanently deleted.");
          void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
          setOpen(false);
          onDeleted();
        },
        onError: () => toast.error("Permanent delete failed."),
      },
    );
  };

  return (
    <Collapsible>
      <CollapsibleTrigger
        className={cn(buttonVariants({ variant: "outline" }), "w-full border-destructive/40 text-destructive")}
      >
        Danger zone
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 rounded border border-destructive/30 bg-destructive/5 p-4">
        <p className="mb-3 text-sm text-muted-foreground">
          Permanently delete this user from Postgres, WorkOS, and billing systems. This cannot be undone.
        </p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="destructive" size="sm">
              Permanently delete user
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Permanently delete user?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the user and related records from all integrated systems. Type the user&apos;s full email
                address to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="flex flex-col gap-3 py-2">
              <div className="flex items-center gap-2">
                <Checkbox id="force-confirm" checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} />
                <Label htmlFor="force-confirm">I understand this action is irreversible</Label>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="force-email">Email confirmation</Label>
                <Input
                  id="force-email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder={email}
                />
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={!canDelete || forceDelete.isPending} onClick={handleForceDelete}>
                Delete permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CollapsibleContent>
    </Collapsible>
  );
};
